import React from 'react';
import {
    Box,
    Flex,
    Text,
    Modal,
    Button,
    Checkbox,
    HStack,
    Divider,
    CloseButton,
    Heading,
} from '@shared/ui';
import { ProductCard } from 'app/components/ProductCard';
import i18n from '@shared/i18n';
import { replacePlaceholder } from 'libs/replacePlaceholder';
import { SpecSelection } from '../SpecSelection';
import { checkProductSoldOut } from '../Giveaway/utils';

interface LaptopModalProps {
    cartId: string | null;
    visible: boolean;
    handleClose: () => void;
    promoThreshold: string;
    promoCopy: PromotionalCopy | null;
    promos: PromoOrderGiveawayPromos[];
    selectedProduct: {
        product_id: string;
        promo_id: string;
        add_on_limit: number;
        spec_details: BuyerPromoProductSpecDetail[];
    } | null;
    selectedSpecs: BuyerPromoProductSpecDetail | null;
    handleSelectProduct: (product: BuyerPromoProduct) => void;
    setSelectedSpecs: (specDetail: BuyerPromoProductSpecDetail) => void;
    isAllSpecsSelected: () => boolean;
    handleSubmit: () => void;
    isSubmitting: boolean;
}

export const LaptopModal: React.FC<LaptopModalProps> = ({
    cartId,
    visible,
    handleClose,
    promoThreshold,
    promoCopy,
    promos,
    selectedProduct,
    selectedSpecs,
    handleSelectProduct,
    setSelectedSpecs,
    isAllSpecsSelected,
    handleSubmit,
    isSubmitting,
}) => {
    return (
        <Modal
            sx={{ maxW: '546px' }}
            isOpen={visible}
            onClose={handleClose}
            closeOnOverlayClick
        >
            <Box p="m">
                <CloseButton
                    position="absolute"
                    top="16px"
                    left="16px"
                    w="16px"
                    h="16px"
                    onClick={handleClose}
                />
                <Heading
                    as="h3"
                    fontSize="sm"
                    mb="s"
                    fontWeight="bold"
                    textAlign="center"
                >
                    {i18n.gettext('訂單滿額贈')}
                </Heading>
                <Divider color="primary.020" />
                <Box
                    maxH={{
                        sm: 'unset',
                        md: '600px',
                    }}
                    overflowY="scroll"
                >
                    {promoThreshold && (
                        <Flex my="15px" bg="neutral.010" py="11px" px="15px">
                            {promoCopy &&
                                replacePlaceholder(promoCopy.text, promoCopy.placeholder)}
                        </Flex>
                    )}
                    <Flex flexDirection="column" gap="m">
                        <Text fontSize="sm" fontWeight="bold">
                            {i18n.gettext('選取贈品')}
                        </Text>
                        {promos &&
                            !!promos.length &&
                            promos.map((promo) => {
                                const isQualified = promo.is_qualified;

                                return (
                                    <React.Fragment
                                        key={'cart-order-giveaway-' + promo.id}
                                    >
                                        {promo.satisfy && (
                                            <Flex
                                                fontWeight="bold"
                                                bg="secondary.010"
                                                p="xs"
                                            >
                                                {replacePlaceholder(
                                                    promo.satisfy.text,
                                                    promo.satisfy.placeholder
                                                )}
                                            </Flex>
                                        )}
                                        {promo.shortfall && (
                                            <Flex
                                                fontWeight="bold"
                                                bg="neutral.010"
                                                p="xs"
                                            >
                                                {replacePlaceholder(
                                                    promo.shortfall.text,
                                                    promo.shortfall.placeholder
                                                )}
                                            </Flex>
                                        )}
                                        <Flex
                                            sx={{
                                                opacity: isQualified ? 1 : 0.3,
                                                pointerEvents: isQualified
                                                    ? 'auto'
                                                    : 'none',
                                            }}
                                            flexDirection="column"
                                            gap="m"
                                            key={'cart-order-giveaway-' + promo.id}
                                        >
                                            {promo.products.map((product) => {
                                                const isChecked =
                                                    selectedProduct?.product_id ===
                                                        product.id &&
                                                    selectedProduct?.promo_id ===
                                                        promo.id;

                                                const isSoldOut = checkProductSoldOut(
                                                    product,
                                                    !!cartId
                                                );

                                                const isDisabled =
                                                    !isQualified ||
                                                    isSoldOut ||
                                                    (selectedProduct && !isChecked);

                                                return (
                                                    <Box
                                                        key={
                                                            'cart-order-giveaway-product-' +
                                                            product.id
                                                        }
                                                        sx={{
                                                            opacity: isDisabled ? 0.3 : 1,
                                                            pointerEvents: isDisabled
                                                                ? 'none'
                                                                : 'auto',
                                                        }}
                                                    >
                                                        <HStack>
                                                            <Checkbox
                                                                isChecked={isChecked}
                                                                onChange={() => {
                                                                    handleSelectProduct(
                                                                        product
                                                                    );
                                                                }}
                                                            />
                                                            <ProductCard.CartGiveaway
                                                                productId={product.id}
                                                                name={product.name}
                                                                badge={product.badge}
                                                                addOnLimit={
                                                                    product.add_on_limit
                                                                }
                                                                image={product.image}
                                                                quantity={
                                                                    product.add_on_limit
                                                                }
                                                                onChange={() => {}}
                                                                isSoldOut={isSoldOut}
                                                                isAddonProduct
                                                                showQuantityOnly
                                                            />
                                                        </HStack>
                                                        {product.spec_details.length >
                                                            0 && (
                                                            <Box my="xs">
                                                                <SpecSelection
                                                                    addonIndex={0}
                                                                    specDetails={
                                                                        product.spec_details
                                                                    }
                                                                    specMetas={
                                                                        product.spec_metas
                                                                    }
                                                                    onSelect={(
                                                                        specDetail
                                                                    ) =>
                                                                        setSelectedSpecs(
                                                                            specDetail as BuyerPromoProductSpecDetail
                                                                        )
                                                                    }
                                                                    selectedSpecs={[
                                                                        selectedSpecs as BuyerPromoProductSpecDetail,
                                                                    ]}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Flex>
                                    </React.Fragment>
                                );
                            })}
                    </Flex>
                </Box>
                <Divider mb="m" color="primary.020" />
                <Flex h="40px">
                    <Button
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        isDisabled={!selectedProduct || !isAllSpecsSelected()}
                    >
                        {i18n.gettext('確定')}
                    </Button>
                </Flex>
            </Box>
        </Modal>
    );
};
